/** Services Import */
import Commons from './Services/Common/Common';
/** Components Import */
import { ThemeProvider } from '@mui/material/styles';
import Routers from './Routers/Router';
import { Loading, Snackbar, DetailResultDialog } from '../src/Components/MUIs';
import VersionUpdateDialog from './Components/VersionUpdateDialog';
/** ag-Grid */
import { LicenseManager } from 'ag-grid-enterprise';
import theme from './Components/Theme/color';
import { useEffect, useState } from 'react';
import InAppNoti from './Components/Ui/InAppNoti';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import ReactQueryProvider from './Routers/ReactQueryProvider';
import { usePerformanceMonitor } from './hooks/usePerformence';
import { useDispatch, useSelector } from 'react-redux';
import {
  closeDetailDialog,
  openDetailDialog,
} from './Reducer/DetailDialogReducer';

LicenseManager.setLicenseKey(process.env.REACT_APP_AgGrid_License);

/**
 * 커스텀 훅: 주기적 버전 체크
 * interval: 체크 주기(ms)
 */
function useVersionCheck(interval = 300000) {
  const [versionDialogOpen, setVersionDialogOpen] = useState(false);

  useEffect(() => {
    const checkVersion = async () => {
      try {
        const res = await fetch('/version.json', { cache: 'no-store' });
        const data = await res.json();
        const localVersion =
          Commons.Storages('get', 'version')?.version || null;

        if (!localVersion) {
          Commons.Storages('set', 'version', { version: data.version });
          return;
        }

        if (localVersion !== data.version) {
          Commons.Storages('set', 'version', { version: data.version });
          setVersionDialogOpen(true);
        }
      } catch (e) {
        console.error('version check error:', e);
      }
    };

    // 초기 체크
    checkVersion();

    // interval 주기마다 체크
    const timer = setInterval(checkVersion, interval);
    return () => clearInterval(timer);
  }, [interval]);

  return [versionDialogOpen, setVersionDialogOpen];
}

function App() {
  const [versionDialogOpen] = useVersionCheck(300000); // 5분 주기
  const dispatch = useDispatch();
  const detailDialog = useSelector((state) => state.detailDialog);

  const meta = document.createElement('meta');
  meta.name = 'viewport';
  meta.content = 'width=device-width, initial-scale=1';
  document.getElementsByTagName('head')[0].appendChild(meta);

  // 전역 스낵바 상세 클릭 이벤트 리스너
  useEffect(() => {
    const handleSnackbarDetailClick = (event) => {
      if (event.detail) {
        dispatch(openDetailDialog({ detailData: event.detail }));
      }
    };

    window.addEventListener('snackbar-detail-click', handleSnackbarDetailClick);

    return () => {
      window.removeEventListener(
        'snackbar-detail-click',
        handleSnackbarDetailClick
      );
    };
  }, [dispatch]);

  // 로컬 스토리지 권한 관리
  if (
    !Commons.Storages('get', 'auth') ||
    !Commons.Storages('get', 'auth').auth
  ) {
    Commons.Storages('set', 'auth', {
      auth: false,
    });
  }

  // 로컬 스토리지 Settings 관리
  const settings = Commons.Storages('get', 'settings');
  if (!settings) {
    // settings가 아예 없을 때만 초기화
    Commons.Storages('set', 'settings', {
      language: 'kr',
      userId: '',
      lanCd: 'Kr',
      userIdKeep: false,
      loginKeep: false,
    });
  } else {
    // settings가 있으면 누락된 필드만 기본값으로 설정 (기존 값은 유지)
    const updatedSettings = {
      language: settings.language || 'kr',
      userId: settings.userId || '',
      lanCd: settings.lanCd || 'Kr',
      userIdKeep:
        settings.userIdKeep !== undefined ? settings.userIdKeep : false,
      loginKeep: settings.loginKeep !== undefined ? settings.loginKeep : false,
    };
    // 기존에 있던 다른 필드들도 유지 (예: userNm)
    Commons.Storages('set', 'settings', {
      ...settings,
      ...updatedSettings,
    });
  }

  // 새로고침 핸들러 (캐시 무시)
  const handleReload = () => {
    // 쿼리 파라미터와 index.html 제거 후 새로고침
    let url = window.location.href.split('?')[0];
    url = url.replace(/\/index\.html$/, '/').replace(/index\.html$/, '');
    window.location.href = url;
  };
  // usePerformanceMonitor({
  //   enableApiTracking: true,
  //   trackResources: true,
  //   logOnUserAction: true, // 사용자 동작 시 로그
  //   userActionTypes: ['click', 'keydown', 'scroll'], // 추적할 이벤트
  //   debounceMs: 2000, // 2초 이내 중복 방지
  // });
  return (
    <ThemeProvider theme={theme}>
      <ReactQueryProvider>
        <InAppNoti />
        <Snackbar />
        <Loading />
        {!versionDialogOpen && <Routers />}
        <VersionUpdateDialog open={versionDialogOpen} onReload={handleReload} />
        {/* 전역 상세 내역 다이얼로그 */}
        <DetailResultDialog
          open={detailDialog.open}
          onClose={() => dispatch(closeDetailDialog())}
          detailData={detailDialog.detailData}
          title={detailDialog.title}
          closeButtonName={detailDialog.closeButtonName}
          maxWidth={detailDialog.maxWidth}
        />
        <ReactQueryDevtools initialIsOpen={true} />
      </ReactQueryProvider>
    </ThemeProvider>
  );
}

export default App;
